import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { founders, inHouseNote, teamCapabilities, teamStatement } from '../../data/deckSlide15';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2;
const COLUMN_X = [112, 852];
const COLUMN_W = 636;
export function TeamSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    timers.push(window.setTimeout(() => setStage(1), 700));
    timers.push(window.setTimeout(() => setStage(2), 2400));
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2691" data-mp="24jd89lziovk0">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2692" data-mp="24iv7cc1wtt5r">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% -20%, rgba(0,200,83,0.06) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2693" data-mp="1zktnhowntpt8" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2694" data-mp="1usbod87uijmt">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2695" data-mp="174bzkdu3h3av" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2696" data-mp="1b6gpb1hf9fs8">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2697" data-mp="1fy74plv7utqi">
          16 · Team
        </motion.span>

        {/* Headline */}
        <motion.h1 className="absolute text-[40px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 126,
        width: 1000
      }} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.15,
        ease
      }} data-id="element-2698" data-mp="1lsoxwx6j74a4">
          Operators who have built telecom, mobility and platforms.
        </motion.h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 240,
        width: 880
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.6,
        ease
      }} data-id="element-2699" data-mp="12ucmt5w27kbw">
          A founding team with deep telecom, mobility-platform, consumer-app and
          technology experience — the exact intersection VEXU is built on.
        </motion.p>

        {/* Founders */}
        {founders.map((founder, i) => <motion.div key={founder.name} className="absolute" style={{
        left: COLUMN_X[i],
        top: 322,
        width: COLUMN_W
      }} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: shown(1) ? 1 : 0,
        y: shown(1) ? 0 : 12
      }} transition={{
        duration: 0.9,
        delay: i * 0.2,
        ease
      }} data-id="element-2700" data-mp="2vpvq4hdeg388" data-mpi={i}>
            <div className="flex items-center gap-6" data-id="element-2701" data-mp="3eh6eaqrqq1wf">
              <div className="shrink-0 overflow-hidden rounded-full" style={{
            width: 108,
            height: 108,
            boxShadow: '0 18px 40px -26px rgba(11,23,18,0.45)',
            border: '1px solid rgba(11,23,18,0.06)'
          }} data-id="element-2702" data-mp="3eihksycklnr8">
                <img src={founder.photo} alt={`Portrait of ${founder.name}`} className="h-full w-full object-cover" style={{
              objectPosition: founder.photoPosition ?? '50% 30%',
              transform: `scale(${founder.photoScale ?? 1})`,
              transformOrigin: founder.photoPosition ?? '50% 30%'
            }} data-id="element-2703" data-mp="39o3j6e7beke4" />
              </div>
              <div data-id="element-2704" data-mp="24krqket5vr72">
                <p className="text-[26px] font-semibold tracking-[-0.02em] text-[#0B1712]" data-id="element-2705" data-mp="24mccxh5bo3tu">
                  {founder.name}
                </p>
                <p className="mt-1 text-[14px] text-[#5A6B60]" data-id="element-2706" data-mp="2x96sq0r72f0z">
                  {founder.role}
                </p>
                <p className="mt-2.5 text-[11.5px] font-semibold uppercase tracking-[0.14em] text-vexu-700" data-id="element-2707" data-mp="2sb5afi1lejqk">
                  {founder.expertise}
                </p>
              </div>
            </div>

            <div className="mt-7 space-y-3 border-t border-[#0B1712]/[0.09] pt-6" data-id="element-2708" data-mp="2ij9cq83f4vmv">
              {founder.experience.map(item => <div key={item} className="flex items-start gap-3" data-id="element-2709" data-mp="3g19f7sqiopj5">
                  <span className="mt-[7px] shrink-0 rounded-full" style={{
              width: 4,
              height: 4,
              backgroundColor: 'rgba(0,153,64,0.5)'
            }} data-id="element-2710" data-mp="3b757365imbgh" />
                  <span className="text-[14px] leading-snug text-[#33463B]" data-id="element-2711" data-mp="3sgzeolps9cnx">
                    {item}
                  </span>
                </div>)}
            </div>
          </motion.div>)}

        {/* Closing */}
        <motion.div className="absolute border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        bottom: 52
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(2) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2712" data-mp="68zql96d72j1">
          <div className="flex items-end justify-between gap-12" data-id="element-2713" data-mp="k1fpbxs8gee5">
            <div data-id="element-2714" data-mp="p2d21uuh4g89">
              <p className="text-[19px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2715" data-mp="p2v8tlericuf">
                {teamStatement}
              </p>
              <p className="mt-2.5 text-[12.5px] tracking-[0.03em] text-[#8A998F]" data-id="element-2716" data-mp="4c97xox8hb94">
                {teamCapabilities}
              </p>
            </div>
            <span className="mb-1 shrink-0 rounded-full border border-vexu/25 bg-vexu/[0.07] px-4 py-2 text-[12.5px] font-semibold text-vexu-700" data-id="element-2717" data-mp="3vk3upr6o5f9m">
              {inHouseNote}
            </span>
          </div>
        </motion.div>
      </div>
    </SlideStage>;
}